import React from 'react';
import { Grid, Box } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { urlImg } from '../../common/helpers';

// Swiper para el carrusel en móvil
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const ServiceImages = ({ id, images = [] }) => {
  const count = images.length;
  const getImgUrl = (imgName) => `${urlImg}/servicios/${id}/${imgName}`;

  const theme = useTheme();
  // En celular/tablet chico mostramos un carrusel; en escritorio, el mosaico original.
  const isMovil = useMediaQuery(theme.breakpoints.down('md'));

  const base = {
    width: '100%',
    objectFit: 'cover',
    borderRadius: '12px',
    display: 'block',
  };

  // Alturas del mosaico de escritorio (md+)
  const hMain = { md: 450 };   // imagen principal
  const hHalf = { md: 221 };   // lado apilado (2 verticales)
  const hTop = { md: 255 };    // superior derecha (4 imgs)
  const hSmall = { md: 187 };  // inferiores en 2 columnas

  // ---------- MÓVIL: carrusel (una imagen por slide) ----------
  if (isMovil) {
    const slides = count === 0 ? [null] : images;
    return (
      <Box
        sx={{
          mb: 3,
          '& .swiper': { borderRadius: '12px' },
          // Flechas
          '& .swiper-button-next, & .swiper-button-prev': {
            color: 'white',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.55),
            width: 34,
            height: 34,
            borderRadius: '50%',
            '&::after': { fontSize: 15, fontWeight: 700 },
          },
          // Puntos
          '& .swiper-pagination-bullet': { backgroundColor: 'white', opacity: 0.6 },
          '& .swiper-pagination-bullet-active': { backgroundColor: 'white', opacity: 1 },
        }}
      >
        <Swiper
          modules={[Navigation, Pagination]}
          slidesPerView={1}
          spaceBetween={0}
          navigation={count > 1}
          pagination={count > 1 ? { clickable: true } : false}
        >
          {slides.map((img, i) => (
            <SwiperSlide key={i}>
              <Box
                component="img"
                src={img ? getImgUrl(img.nombre) : '/search.png'}
                alt={`Imagen ${i + 1}`}
                sx={{ width: '100%', height: 260, objectFit: 'cover', display: 'block' }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
    );
  }

  // ---------- ESCRITORIO: mosaico según cantidad ----------
  // Sin imágenes — placeholder único
  if (count === 0) {
    return (
      <Box component="img" src="/search.png" alt="Sin imagen"
        sx={{ ...base, height: hMain, mb: 1 }} />
    );
  }

  // 1 imagen — ocupa todo el ancho disponible
  if (count === 1) {
    return (
      <Box component="img" src={getImgUrl(images[0].nombre)} alt="Principal"
        sx={{ ...base, height: hMain, mb: 1 }} />
    );
  }

  // 2 imágenes — principal más ancha + secundaria al lado
  if (count === 2) {
    return (
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid size={{ md: 8 }}>
          <Box component="img" src={getImgUrl(images[0].nombre)} alt="Principal"
            sx={{ ...base, height: hMain }} />
        </Grid>
        <Grid size={{ md: 4 }}>
          <Box component="img" src={getImgUrl(images[1].nombre)} alt="Imagen 2"
            sx={{ ...base, height: { md: 450 } }} />
        </Grid>
      </Grid>
    );
  }

  // 3 imágenes — grande izquierda + 2 apiladas derecha
  if (count === 3) {
    return (
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid size={{ md: 7 }}>
          <Box component="img" src={getImgUrl(images[0].nombre)} alt="Principal"
            sx={{ ...base, height: hMain }} />
        </Grid>
        <Grid size={{ md: 5 }}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }}>
              <Box component="img" src={getImgUrl(images[1].nombre)} alt="Imagen 2"
                sx={{ ...base, height: hHalf }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box component="img" src={getImgUrl(images[2].nombre)} alt="Imagen 3"
                sx={{ ...base, height: hHalf }} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  // 4 imágenes — grande izquierda + 1 arriba derecha + 2 abajo derecha
  if (count === 4) {
    return (
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid size={{ md: 7 }}>
          <Box component="img" src={getImgUrl(images[0].nombre)} alt="Principal"
            sx={{ ...base, height: hMain }} />
        </Grid>
        <Grid size={{ md: 5 }}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }}>
              <Box component="img" src={getImgUrl(images[1].nombre)} alt="Imagen 2"
                sx={{ ...base, height: hTop }} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box component="img" src={getImgUrl(images[2].nombre)} alt="Imagen 3"
                sx={{ ...base, height: hSmall }} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box component="img" src={getImgUrl(images[3].nombre)} alt="Imagen 4"
                sx={{ ...base, height: hSmall }} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  // 5+ imágenes — layout completo: grande izquierda + grid 2x2 derecha
  return (
    <Grid container spacing={1} sx={{ mb: 1 }}>
      <Grid size={{ md: 7 }}>
        <Box component="img" src={getImgUrl(images[0].nombre)} alt="Principal"
          sx={{ ...base, height: hMain }} />
      </Grid>
      <Grid size={{ md: 5 }}>
        <Grid container spacing={1}>
          {images.slice(1, 5).map((img, i) => (
            <Grid size={{ xs: 6 }} key={i}>
              <Box component="img" src={getImgUrl(img.nombre)} alt={`Imagen ${i + 2}`}
                sx={{ ...base, height: hHalf }} />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ServiceImages;
